import { useTranslation } from 'react-i18next';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type CtlReceiptExplanationValues = {
  kind?: string | null;
  typeCode?: string | null;
  versionCode?: string | null;
  acknowledgementCode?: string | null;
  envelopeIdentifier?: string | null;
  letterIdentifier?: string | null;
  senderEan?: string | null;
  receiverEan?: string | null;
  originalEnvelopeIdentifier?: string | null;
  originalLetterIdentifier?: string | null;
  refuseCode?: string | null;
  refuseReason?: string | null;
  generatedReceiptKind?: string | null;
  createdAt?: string | null;
  importedAt?: string | null;
};

function Row({
  label,
  value,
  tone,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  tone?: 'error';
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt
        className={cn(
          'w-48 shrink-0 font-medium',
          tone === 'error' ? 'text-ds-red-ink' : undefined,
        )}
      >
        {label}
      </dt>
      <dd
        className={cn(
          tone === 'error' ? 'whitespace-pre-line text-ds-red-ink' : undefined,
          mono ? 'font-mono' : undefined,
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Shared explanation for a generated or imported XCONTRL receipt: refuse/error
 * code, explanation text, referenced original message, and timestamps.
 */
export function CtlReceiptExplanation({
  values,
  formatTs,
}: {
  values: CtlReceiptExplanationValues;
  formatTs?: (iso: string) => string;
}) {
  const { t } = useTranslation();
  const stamp = (iso: string | null | undefined) =>
    iso ? (formatTs ? formatTs(iso) : iso) : null;

  return (
    <dl className={cn(TYPE.body, 'space-y-1 text-xs text-ds-text')}>
      <Row label={t('communicate.col_type')} value={values.kind ?? values.typeCode} />
      <Row label={t('medcomSection.exchange_col_version')} value={values.versionCode} />
      <Row label={t('medcomSection.exchange_col_ack')} value={values.acknowledgementCode} />
      <Row
        label={t('medcomSection.exchange_col_envelope')}
        value={values.envelopeIdentifier}
        mono
      />
      <Row label={t('medcomSection.exchange_col_letter')} value={values.letterIdentifier} mono />
      <Row label={t('communicate.col_sender')} value={values.senderEan} />
      <Row label={t('communicate.col_receiver')} value={values.receiverEan} />
      <Row
        label={t('medcomSection.exchange_col_original_envelope')}
        value={values.originalEnvelopeIdentifier}
        mono
      />
      <Row
        label={t('medcomSection.exchange_col_original_letter')}
        value={values.originalLetterIdentifier}
        mono
      />
      <Row
        label={t('medcomSection.exchange_col_refuse')}
        value={values.refuseCode}
        tone={values.refuseCode ? 'error' : undefined}
      />
      <Row
        label={t('medcomSection.exchange_col_refuse_reason')}
        value={values.refuseReason}
        tone={values.refuseReason ? 'error' : undefined}
      />
      <Row label={t('medcomSection.exchange_col_generated')} value={values.generatedReceiptKind} />
      <Row label={t('medcomSection.exchange_col_imported_at')} value={stamp(values.importedAt)} />
      <Row label={t('medcomSection.exchange_col_created_at')} value={stamp(values.createdAt)} />
    </dl>
  );
}
